<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import Button from "./Button.vue";
import Icon from "./Icon.vue";
import Modal from "./Modal.vue";
import MfaSetup from "./MfaSetup.vue";
import RecoveryCodes from "./RecoveryCodes.vue";
import StatusChip from "./StatusChip.vue";
import { api } from "../api/client";
import { errorText } from "../api/errors";
import { toast } from "../composables/feedback";
import { session } from "../composables/session";
import { locale, t } from "../i18n";

/**
 * "Minha conta": the password and two-step verification. Verification is set up with a QR code
 * read by an authenticator app, confirmed by a first code, and hands out ten recovery codes once.
 */
const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

interface MfaStatus {
  enabled: boolean;
  enabledAt: string | null;
  recoveryCodesLeft: number;
}
const status = ref<MfaStatus | null>(null);
type Step = "idle" | "setup" | "codes" | "disable" | "regenerate" | "password";
const step = ref<Step>("idle");
const busy = ref(false);
const code = ref("");
const pass = ref("");
const next = ref("");
const confirm = ref("");
const recoveryCodes = ref<string[]>([]);
const codeInput = ref<HTMLInputElement>();

async function load() {
  try {
    status.value = await api<MfaStatus>("/v1/auth/mfa");
  } catch (err) {
    toast(errorText(err));
  }
}
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    step.value = "idle";
    reset();
    void load();
  },
  { immediate: true },
);
function reset() {
  code.value = "";
  pass.value = "";
  next.value = "";
  confirm.value = "";
}
async function focusCode() {
  await nextTick();
  codeInput.value?.focus();
}

const since = computed(() =>
  status.value?.enabledAt
    ? new Date(status.value.enabledAt).toLocaleDateString(locale.value, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "",
);

async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
  busy.value = true;
  try {
    return await fn();
  } catch (err) {
    toast(errorText(err));
    return undefined;
  } finally {
    busy.value = false;
  }
}

function startSetup() {
  step.value = "setup";
}
async function finishSetup() {
  step.value = "idle";
  await load();
}
async function regenerate() {
  const r = await run(() =>
    api<{ recoveryCodes: string[] }>("/v1/auth/mfa/recovery-codes", {
      method: "POST",
      body: { code: code.value.trim() },
    }),
  );
  if (!r) return;
  recoveryCodes.value = r.recoveryCodes;
  step.value = "codes";
  await load();
}
async function disable() {
  const r = await run(() =>
    api("/v1/auth/mfa/disable", {
      method: "POST",
      body: { password: pass.value, code: code.value.trim() },
    }),
  );
  if (r === undefined) return;
  toast(t("account.mfa.disabled"));
  if (session.me) session.me.user.mfaEnabled = false;
  step.value = "idle";
  reset();
  await load();
}
async function changePassword() {
  if (next.value !== confirm.value) {
    toast(t("account.password.mismatch"));
    return;
  }
  const r = await run(() =>
    api("/v1/auth/me/password", {
      method: "POST",
      body: { current: pass.value, next: next.value },
    }),
  );
  if (r === undefined) return;
  toast(t("account.password.changed"));
  step.value = "idle";
  reset();
}

function cancel() {
  step.value = "idle";
  reset();
}
</script>
<template>
  <Modal :open="open" :title="t('account.title')" @close="emit('close')">
    <div class="account">
      <p class="small muted">
        {{ session.me?.user.name }} · {{ session.me?.user.email }}
      </p>

      <RecoveryCodes
        v-if="step === 'codes'"
        :codes="recoveryCodes"
        @done="cancel"
      />
      <MfaSetup
        v-else-if="step === 'setup'"
        cancellable
        @done="finishSetup"
        @cancel="cancel"
      />

      <template v-else>
        <section class="account-section">
          <div class="row">
            <Icon name="shield" :size="20" class="green" />
            <h3 class="grow">{{ t("account.mfa.title") }}</h3>
            <StatusChip
              v-if="status"
              :status="status.enabled ? 'success' : 'neutral'"
              :label="
                status.enabled ? t('account.mfa.on') : t('account.mfa.off')
              "
            />
          </div>
          <p class="small muted">
            {{
              status?.enabled
                ? t("account.mfa.on_lead", { since })
                : t("account.mfa.off_lead")
            }}
          </p>
          <template v-if="status?.enabled">
            <p class="small" :class="{ danger: status.recoveryCodesLeft <= 2 }">
              {{
                t("account.mfa.codes_left", {
                  count: status.recoveryCodesLeft,
                })
              }}
            </p>
            <form
              v-if="step === 'regenerate' || step === 'disable'"
              class="account-form"
              @submit.prevent="step === 'disable' ? disable() : regenerate()"
            >
              <label v-if="step === 'disable'" class="wa-field"
                ><span>{{ t("account.password.current") }}</span
                ><input
                  v-model="pass"
                  type="password"
                  autocomplete="current-password"
                  required
              /></label>
              <label class="wa-field"
                ><span>{{ t("account.mfa.code_or_recovery") }}</span
                ><input
                  ref="codeInput"
                  v-model="code"
                  autocomplete="one-time-code"
                  maxlength="11"
                  required
              /></label>
              <div class="row account-actions">
                <span class="grow" />
                <Button variant="secondary" type="button" @click="cancel">{{
                  t("common.cancel")
                }}</Button>
                <Button
                  type="submit"
                  :variant="step === 'disable' ? 'danger' : 'primary'"
                  :loading="busy"
                  >{{
                    step === "disable"
                      ? t("account.mfa.disable")
                      : t("account.mfa.regenerate")
                  }}</Button
                >
              </div>
            </form>
            <div v-else class="row account-actions">
              <Button
                variant="secondary"
                compact
                @click="((step = 'regenerate'), focusCode())"
                >{{ t("account.mfa.regenerate") }}</Button
              >
              <Button
                variant="ghost"
                compact
                @click="((step = 'disable'), focusCode())"
                >{{ t("account.mfa.disable") }}</Button
              >
            </div>
          </template>
          <div v-else-if="status" class="row account-actions">
            <Button compact :loading="busy" @click="startSetup">{{
              t("account.mfa.enable")
            }}</Button>
          </div>
        </section>

        <section class="account-section">
          <div class="row">
            <Icon name="key" :size="20" class="green" />
            <h3 class="grow">{{ t("account.password.title") }}</h3>
          </div>
          <form
            v-if="step === 'password'"
            class="account-form"
            @submit.prevent="changePassword"
          >
            <label class="wa-field"
              ><span>{{ t("account.password.current") }}</span
              ><input
                v-model="pass"
                type="password"
                autocomplete="current-password"
                required
            /></label>
            <label class="wa-field"
              ><span>{{ t("account.password.new") }}</span
              ><input
                v-model="next"
                type="password"
                autocomplete="new-password"
                minlength="10"
                required
            /></label>
            <label class="wa-field"
              ><span>{{ t("account.password.confirm") }}</span
              ><input
                v-model="confirm"
                type="password"
                autocomplete="new-password"
                minlength="10"
                required
            /></label>
            <p class="small muted">{{ t("account.password.hint") }}</p>
            <div class="row account-actions">
              <span class="grow" />
              <Button variant="secondary" type="button" @click="cancel">{{
                t("common.cancel")
              }}</Button>
              <Button type="submit" :loading="busy">{{
                t("account.password.save")
              }}</Button>
            </div>
          </form>
          <div v-else class="row account-actions">
            <Button variant="secondary" compact @click="step = 'password'">{{
              t("account.password.change")
            }}</Button>
          </div>
        </section>
      </template>
    </div>
  </Modal>
</template>
